/**
 * CENTRALIZED BUTTON STYLE CONFIGURATION
 * 
 * Change colors here and they will apply to ALL buttons across the platform.
 * This file provides the single source of truth for button colors.
 */

export const BUTTON_COLORS = {
  // Purple (Generic/Navigation buttons)
  purple: '#6c3082',
  purpleGradient: 'linear-gradient(135deg, #6c3082 0%, #8e44ad 100%)',
  purpleBorder: 'rgba(108, 48, 130, 0.3)',
  purpleLight: 'rgba(108, 48, 130, 0.08)',
  
  // Green (Create/Add buttons) - Light pastel green matching read article rows
  green: '#66bb6a',
  greenGradient: 'linear-gradient(135deg, #66bb6a, #81c784)',
  greenBorder: 'rgba(102, 187, 106, 0.3)',
  
  // Red (Delete/Remove buttons)
  red: '#f44336',
  redGradient: 'linear-gradient(135deg, #f44336, #ef5350)',
  redBorder: 'rgba(244, 67, 54, 0.3)',
  
  // Gray (Inactive buttons)
  grayDark: 'rgba(255, 255, 255, 0.1)',
  grayLight: 'rgba(0, 0, 0, 0.08)',
  
  // Borders (Inactive)
  borderInactiveDark: 'rgba(255, 255, 255, 0.3)',
  borderInactiveLight: 'rgba(0, 0, 0, 0.2)',
  
  // Hover
  borderHover: '#6c3082',
  hoverFill: 'rgba(108, 48, 130, 0.15)',
  
  // Text
  textWhite: '#ffffff',
  textPrimary: 'var(--text-primary)'
};

/**
 * Helper function to get button styles for Material-UI sx prop
 * Use this for consistent button styling across the platform
 */
export const getButtonStyles = (isActive, isDarkMode, type = 'generic') => {
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1.5rem',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
    fontWeight: isActive ? 600 : 500,
    fontSize: '0.95rem',
    textDecoration: 'none',
    cursor: 'pointer'
  };

  // Generic buttons (purple when active)
  if (type === 'generic') {
    return {
      ...baseStyle,
      background: isActive 
        ? BUTTON_COLORS.purpleGradient 
        : isDarkMode ? BUTTON_COLORS.grayDark : BUTTON_COLORS.grayLight,
      color: `${isActive ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important`,
      border: isActive 
        ? `1px solid ${BUTTON_COLORS.purpleBorder} !important` 
        : '1px solid transparent',
      '& .MuiSvgIcon-root': {
        color: `${isActive ? BUTTON_COLORS.textWhite : BUTTON_COLORS.textPrimary} !important`
      },
      '&:hover': {
        background: isActive ? BUTTON_COLORS.purpleGradient : BUTTON_COLORS.hoverFill,
        borderColor: `${BUTTON_COLORS.borderHover} !important`,
        opacity: isActive ? 0.9 : 1
      }
    };
  }
  
  // Create buttons (always green)
  if (type === 'create') {
    return {
      ...baseStyle,
      background: BUTTON_COLORS.greenGradient,
      color: `${BUTTON_COLORS.textWhite} !important`,
      border: '1px solid transparent',
      '& .MuiSvgIcon-root': {
        color: `${BUTTON_COLORS.textWhite} !important`
      },
      '&:hover': {
        opacity: 0.9
      }
    };
  }
  
  // Delete buttons (always red)
  if (type === 'delete') {
    return {
      ...baseStyle,
      background: BUTTON_COLORS.redGradient,
      color: `${BUTTON_COLORS.textWhite} !important`,
      border: '1px solid transparent',
      '& .MuiSvgIcon-root': {
        color: `${BUTTON_COLORS.textWhite} !important`
      },
      '&:hover': {
        opacity: 0.9
      }
    };
  }
  
  return baseStyle;
};

